import { useEffect, useMemo } from 'react'
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook'
import { v4 } from 'uuid'

export const COMMENT_ESC_SCOPE_PREFIX = 'comment-esc'

/**
 * Scoped Escape handler for the in-scene comment tooling. When `enabled`, it
 * activates a dedicated hotkey scope so the comment Escape action is the only
 * one applied – other scoped Esc handlers (e.g. structural planning) defer while
 * a comment scope is active (they exclude this prefix from their `enabled`).
 */
const useCommentEsc = (callback: () => void, enabled: boolean) => {
  const scopeId = useMemo(() => `${COMMENT_ESC_SCOPE_PREFIX}-${v4()}`, [])

  const { enableScope, disableScope } = useHotkeysContext()

  useEffect(() => {
    if (enabled) enableScope(scopeId)
    else disableScope(scopeId)

    return () => disableScope(scopeId)
  }, [enabled, enableScope, disableScope, scopeId])

  useHotkeys('esc', callback, { scopes: [scopeId], enabled: true }, [callback])
}

/**
 * Whether a comment Esc scope is currently active. Other Escape handlers should
 * disable themselves while this is true so the comment Escape action wins.
 */
export const useIsCommentEscScopeActive = (): boolean => {
  const { enabledScopes } = useHotkeysContext()

  return useMemo(
    () => enabledScopes.some(scope => scope.startsWith(COMMENT_ESC_SCOPE_PREFIX)),
    [enabledScopes],
  )
}

export default useCommentEsc
